import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  TrendingUp,
  AlertTriangle,
  FolderOpen,
  Filter,
  Percent,
  Clock,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ProjectRow, type ProjectData } from '../components/dashboard/ProjectRow';
import { api } from '../core/apiClient';

const eur = (v: number) => v.toLocaleString('ca-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
const pct = (v: number) => v.toLocaleString('ca-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
const fmtHores = (v: number) => v.toLocaleString('ca-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface PipelineEstatSnapshot {
  estat: string;
  label: string;
  count: number;
  baseImposableTotal: number;
  costTotal: number;
  margeBrut: number;
  margePercent: number;
  hores: { p: number; f: number; m: number; o: number; e: number; total: number };
}
interface PipelineSnapshot {
  byEstat: PipelineEstatSnapshot[];
  totals: PipelineEstatSnapshot;
}

const PIPELINE_COLOR: Record<string, string> = {
  ACCEPTAT: '#1d4ed8',   // Comanda
  ADJUDICAT: '#f59e0b',  // Proposat Adjudicatari
};

interface TallerProjectRow {
  id: string;
  project_number: string;
  name: string;
  etiqueta_expedient?: string | null;
  brand_code: 'EQ' | 'SR';
  brand_name: string;
  brand_color?: string;
  client_name?: string;
  status: ProjectData['status'];
  category: string;
  estimated_revenue: string | number;
  actual_material_cost: string | number;
  actual_labor_cost: string | number;
  actual_other_cost: string | number;
  estimated_margin: string | number;
  actual_margin: string | number;
  start_date?: string;
  actual_end_date?: string | null;
}

function mapTallerProject(row: TallerProjectRow): ProjectData {
  const materialCost = Number(row.actual_material_cost) || 0;
  const laborCost = Number(row.actual_labor_cost) || 0;
  const otherCost = Number(row.actual_other_cost) || 0;
  return {
    id: row.id,
    projectNumber: row.project_number,
    name: row.etiqueta_expedient || row.name,
    brandCode: row.brand_code,
    brandName: row.brand_name,
    brandColor: row.brand_color,
    clientName: row.client_name ?? '',
    status: row.status,
    category: row.category,
    estimatedBudget: Number(row.estimated_revenue) || 0,
    actualCost: materialCost + laborCost + otherCost,
    estimatedMargin: Number(row.estimated_margin) || 0,
    actualMargin: Number(row.actual_margin) || 0,
    laborCost,
    materialCost,
    startDate: row.start_date ?? '',
    endDate: row.actual_end_date ?? undefined,
  };
}

const HORES_CATEGORIES: { key: 'p' | 'f' | 'm' | 'o' | 'e'; label: string; color: string }[] = [
  { key: 'p', label: 'Taller',     color: '#1d4ed8' },
  { key: 'f', label: 'Fabricació', color: '#8b5cf6' },
  { key: 'm', label: 'Muntatge',   color: '#f59e0b' },
  { key: 'o', label: 'Oficina',    color: '#10b981' },
  { key: 'e', label: 'Extern',     color: '#64748b' },
];
const CARDS = [
  { label: 'Facturació Total',  description: 'Ingrés total del període',   icon: TrendingUp,  color: 'blue',   link: '/facturacio' },
  { label: 'Marge Promig',      description: 'Marge mitjà sobre vendes',   icon: Percent,     color: 'green',  link: '/facturacio' },
  { label: 'Projectes Actius',  description: 'Expedients en curs',         icon: FolderOpen,  color: 'purple', link: '/taller' },
  { label: 'Alertes Estoc',     description: 'Materials en ruptura',       icon: AlertTriangle, color: 'amber', link: '/compres' },
];

const COLOR: Record<string, { border: string; bg: string; icon: string; iconBg: string }> = {
  blue:   { border: 'border-blue-500',   bg: 'bg-blue-50/50',   icon: 'text-blue-500',   iconBg: 'bg-blue-500/10' },
  green:  { border: 'border-green-500',  bg: 'bg-green-50/50',  icon: 'text-green-500',  iconBg: 'bg-green-500/10' },
  purple: { border: 'border-purple-500', bg: 'bg-purple-50/50', icon: 'text-purple-500', iconBg: 'bg-purple-500/10' },
  amber:  { border: 'border-amber-500',  bg: 'bg-amber-50/50',  icon: 'text-amber-500',  iconBg: 'bg-amber-500/10' },
};


// Main Dashboard Component
export default function Dashboard() {
  const navigate = useNavigate();
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [pipeline, setPipeline] = useState<PipelineSnapshot | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    // brand_id és un UUID a /taller/projects, no el codi EQ/SR del select — es
    // demanen els actius sense filtrar i es filtra pel codi al client.
    api
      .get<{ success: boolean; data: TallerProjectRow[] }>('/taller/projects?status=active&limit=50')
      .then((res) => { if (!cancelled) setProjects((res?.data ?? []).map(mapTallerProject)); })
      .catch(() => { if (!cancelled) setProjects([]); })
      .finally(() => { if (!cancelled) setProjectsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPipelineLoading(true);
    const params = brandFilter !== 'all' ? `?brand=${brandFilter}` : '';
    api
      .get<{ success: boolean; data: PipelineSnapshot }>(`/crm-dashboard/pipeline${params}`)
      .then((res) => { if (!cancelled) setPipeline(res?.data ?? null); })
      .catch(() => { if (!cancelled) setPipeline(null); })
      .finally(() => { if (!cancelled) setPipelineLoading(false); });
    return () => { cancelled = true; };
  }, [brandFilter]);

  const pipelineDonutData = (pipeline?.byEstat ?? [])
    .filter((s) => s.count > 0)
    .map((s) => ({ name: s.label, value: s.count, estat: s.estat, color: PIPELINE_COLOR[s.estat] ?? '#94a3b8' }));

  const horesTotal = pipeline?.totals.hores;

  const filteredProjects = brandFilter === 'all' ? projects : projects.filter((p) => p.brandCode === brandFilter);

  return (
    <div className="space-y-8">
      {/* Section Header + Filter */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tauler</h1>
          <p className="text-gray-600 mt-2">Vista general del rendiment de Roig i Fills</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white w-[180px]"
          >
            <option value="all">Totes les marques</option>
            <option value="EQ">Equiro</option>
            <option value="SR">SpokoRamps</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-3">
        {CARDS.map(({ label, icon: Icon, color, link }) => {
          const c = COLOR[color];
          return (
            <Link
              key={label}
              to={link}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-white hover:shadow-sm transition-all"
            >
              <div className={`p-1.5 rounded-lg shrink-0 ${c.iconBg}`}>
                <Icon className={`h-4 w-4 ${c.icon}`} />
              </div>
              <span className="text-xs font-semibold">{label}</span>
            </Link>
          );
        })}
      </div>

      {/* Pipeline Row: Comanda + Proposat Adjudicatari */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="rounded-xl col-span-2">
          <CardHeader>
            <CardTitle>Càrrega de Treball</CardTitle>
            <CardDescription>Pressupostos en Comanda i Proposat Adjudicatari</CardDescription>
          </CardHeader>
          <CardContent>
            {pipelineLoading ? (
              <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">Carregant...</div>
            ) : !pipeline || pipelineDonutData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
                <FolderOpen className="w-10 h-10 opacity-20" />
                <span className="text-sm">Sense pressupostos en Comanda ni Proposat Adjudicatari</span>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 items-center">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart margin={{ top: 16, right: 16, bottom: 16, left: 16 }}>
                    <Pie
                      data={pipelineDonutData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                      label={({ value }: { value: number }) => `${value}`}
                      labelLine={false}
                    >
                      {pipelineDonutData.map((d) => <Cell key={d.estat} fill={d.color} stroke="none" />)}
                    </Pie>
                    <Tooltip formatter={(v: number, _n: string, entry: any) => [`${v} pressupostos`, entry?.payload?.name]} />
                  </PieChart>
                </ResponsiveContainer>

                <div className="space-y-3">
                  {pipeline.byEstat.map((s) => (
                    <div key={s.estat} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-border">
                      <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: PIPELINE_COLOR[s.estat] ?? '#94a3b8' }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold">{s.label}</span>
                          <Badge variant="secondary">{s.count}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mt-0.5">
                          <span>{eur(s.baseImposableTotal)}</span>
                          <span className={s.margePercent < 15 ? 'text-amber-600' : 'text-green-600'}>
                            Marge {pct(s.margePercent)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-xs font-semibold pt-1 border-t">
                    <span>Total</span>
                    <span>{eur(pipeline.totals.baseImposableTotal)} · Marge {pct(pipeline.totals.margePercent)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" />Càrrega d'Hores Prevista</CardTitle>
            <CardDescription>Comanda + Proposat Adjudicatari</CardDescription>
          </CardHeader>
          <CardContent>
            {pipelineLoading ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">Carregant...</div>
            ) : !horesTotal || horesTotal.total <= 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground gap-2">
                <Clock className="w-8 h-8 opacity-20" />
                <span className="text-sm">Sense hores previstes</span>
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className="text-2xl font-bold tabular-nums">{fmtHores(horesTotal.total)} <span className="text-sm font-normal text-muted-foreground">hores</span></div>
                <div className="space-y-1.5">
                  {HORES_CATEGORIES.map(({ key, label, color }) => {
                    const v = horesTotal[key];
                    const w = horesTotal.total > 0 ? (v / horesTotal.total) * 100 : 0;
                    if (v <= 0) return null;
                    return (
                      <div key={key} className="space-y-0.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{label}</span>
                          <span className="tabular-nums font-medium">{fmtHores(v)} h</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${w}%`, backgroundColor: color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Projects Table */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3 border-b bg-primary/5 rounded-t-xl">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-foreground">
              Expedients de Taller
              {brandFilter !== 'all' && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  · {brandFilter === 'EQ' ? 'Equiro' : 'SpokoRamps'}
                </span>
              )}
            </CardTitle>
            <Badge variant="secondary">{filteredProjects.length} actius</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Expedient</TableHead>
                <TableHead>Projecte / Client</TableHead>
                <TableHead className="w-[180px]">Pressupost</TableHead>
                <TableHead>Costos</TableHead>
                <TableHead className="text-right">Marge</TableHead>
                <TableHead>Estat</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProjects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onClick={(p) => navigate(`/taller/expedients/${p.id}`)}
                />
              ))}
            </TableBody>
          </Table>
          {projectsLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">Carregant...</div>
          ) : filteredProjects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
              <FolderOpen className="w-10 h-10 opacity-20" />
              <span className="text-sm">Sense expedients actius</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
